import React, {useCallback, useEffect, useState} from "react"
import {useSessionCookie} from "../hooks/useSessionCookie";
import {
    Avatar,
    Box, Button,
    Card, Checkbox,
    Divider,
    FormControl, FormHelperText,
    FormLabel,
    Grid, Input,
    Stack,
    Step,
    StepIndicator,
    Stepper,
    Typography, useTheme
} from "@mui/joy";
import {DEFAULT_SUBSCRIPTION_PLANS, SubscriptionPlan} from "../models/subscription";
import CheckoutPricingCardComponent from "../components/Subscription/CheckoutPricingCardComponent";
import {useUITheme} from "../hooks/useUITheme";
import {Link, useNavigate} from "react-router-dom";
import Login from "./Login";
import Register, {RegisterPersonalInfoStep} from "./Register";
import {
    ArrowLeft,
    ArrowRight,
    Check,
    CreditCard,
    Eye,
    EyeOff,
    LockIcon,
    LucideMail,
    UserIcon
} from "lucide-react";
import {AuthUser, UserFormData} from "../models/user";
import {Trans, useTranslation} from "react-i18next";
import {Region} from "../models/region";
import {useAsync} from "../hooks/useAsyncResult";
import {createCheckout, initPayment} from "../services/api/transaction";
import LoadingPageComponent from "../components/Loading/LoadingPageComponent";
import {APP_URL, WS_API_HOST} from "../constants";
import {appHost, productionMode} from "../utils/routes";
import {CheckoutSession, TransactionStatus} from "../models/transaction";
import PaymentStatusDialog from "../components/PaymentStatusDialog";
import IkeepayCheckout from "../components/IkeepayCheckout";
import {useFeedback} from "../hooks/useFeedback";
import {useAuth} from "../hooks/useAuth";
import {useSearchParams} from "react-router-dom";
import LoadingError from "../components/Error/LoadingError";
import {trialSubscription} from "../services/api/subscription";

type CheckoutAccountStepProps = {
    onFormDataChange?: (key: keyof UserFormData, value: any) => void,
    fieldValidation?: {
        [key in keyof UserFormData]: {
            required: boolean,
            message?: string,
            check?: boolean
        }
    },
    formData?: UserFormData,
    subscription?: SubscriptionPlan,
}


type CheckoutReviewStepProps = {
    account?: UserFormData,
    subscription?: SubscriptionPlan,
    region?: Region,
    onEditOrderInfo?: (step: number) => void
}

const Checkout = () => {
    const auth = useAuth()
    const {t} = useTranslation()
    const theme = useTheme()
    const {userSession} = useSessionCookie()
    const [searchParams, setSearchParams] = useSearchParams()
    const [userFormData, setUserFormData] = useState<UserFormData>(userSession?.['currentUser'] || {})
    const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan>()
    const [canGoToNext, setCanGoToNext] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)
    const [paymentStatus, setPaymentStatus] = useState<TransactionStatus>()
    const [showPaymentStatusDialog, setShowPaymentStatusDialog] = useState(false)
    const [transactionId, setTransactionId] = useState<string>(null)
    const [checkoutUrl, setCheckoutUrl] = useState<string>()
    const feedback = useFeedback()
    const steps = [{
        name: t("accountStepTitle"),
        actionLabel: t("accountStepActionLabel"),
    }, {
        name: t("confirmStepTitle"),
        actionLabel: selectedPlan?.isTrial ? t("confirmStepActionLabel") : t("confirmAndPayStepActionLabel", {localizedAmount: selectedPlan?.localizedPrice})
    }]
    const navigate = useNavigate()
    const uiTheme = useUITheme()
    const initPaymentAsyncResult = useAsync(initPayment, false, false)
    const createCheckoutAsyncResult = useAsync(createCheckout, true, true, false)
    // The free trial is one per account; the backend enforces it, this just stops offering it.
    const currentUser = userSession?.['currentUser']
    const trialUsed = !!currentUser?.freeTrialDisabled || !!currentUser?.subscription?.isTrial
    const availablePlans = (initPaymentAsyncResult.payload?.subscriptions || [])
        .filter(plan => !(plan.isTrial && trialUsed))
    const trialSubscriptionAsyncResult = useAsync(trialSubscription, true, true, false)

    const accountFieldValidation: {
        [key in keyof UserFormData]: {
            required: boolean,
            message?: string,
            check?: boolean
        }
    } = {
        name: {
            check: new RegExp(/^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,}$/).test(userFormData?.name),
            message: t("invalidNameFieldMessage"),
            required: true
        },
        email: {
            required: true,
            message: t("invalidEmailFieldMessage"),
            check: new RegExp(/^[^\s@]+@[^\s@]+\.[^\s@]+$/).test(userFormData?.email)
        },
        password: {
            check: userFormData?.password?.length >= 6,
            message: t("invalidPasswordFieldMessage"),
            required: true
        },
        confirmPassword: {
            check: userFormData?.confirmPassword === userFormData?.password,
            message: t("invalidConfirmPasswordMessage"),
            required: true
        },
        agreedToTermsAndCondition: {
            check: !!userFormData.agreedToTermsAndCondition,
            message: t("termsAndConditionsNotAgreedMessage"),
            required: true
        }
    }

    /** Signed in, on to the dashboard — carrying the session over to the app subdomain if needed. */
    const goToDashboard = async () => {
        window.location.href = await auth.appEntryUrl()
    }

    const handleSubmit = async () => {
        if (userSession?.['currentUser']) {
            if (selectedPlan.isTrial) {
                await trialSubscriptionAsyncResult.execute()
            } else {
                createCheckoutAsyncResult.execute(selectedPlan?._id)
            }

        } else {
            if (selectedPlan.isTrial) {
                const result = await auth.register({
                    ...userFormData,
                    withFreeTrial: true
                })

                if (result)
                    await goToDashboard()


            } else {
                const paymentRequestResponse =
                    await auth.register({
                        ...userFormData,
                        paymentRequest: {
                            subscriptionId: selectedPlan?._id,
                        }
                    }) as AuthUser['paymentRequestResponse']

                if (paymentRequestResponse)
                    openCheckout(paymentRequestResponse)
            }

        }
    }

    const openCheckout = (checkout: CheckoutSession) => {
        setTransactionId(checkout.transactionId)
        setCheckoutUrl(checkout.checkoutUrl)
    }

    const handleListenPayment = (transactionId: string, initialStatus: TransactionStatus) => {
        const ws = new WebSocket(`${WS_API_HOST}/ws/payments?transactionId=${transactionId}`)
        setPaymentStatus(initialStatus)
        setShowPaymentStatusDialog(true)
        ws.onmessage = (event) => {
            const {status} = JSON.parse(event.data)
            if (status !== "timeout")
                setPaymentStatus(status)
        }
    }

    // Ikeepay only tells the browser the payment went through; the subscription is granted once
    // Ikeepay's webhook reaches the backend, which then pushes the result over the socket.
    const handleCheckoutSuccess = useCallback(() => {
        setCheckoutUrl(undefined)
        handleListenPayment(transactionId, "pending")
    }, [transactionId])

    const handleCheckoutClose = useCallback(() => setCheckoutUrl(undefined), [])

    const handleCheckoutError = useCallback((message?: string) => {
        setCheckoutUrl(undefined)
        feedback.showFeedback(message || t("ikeepayCheckoutErrorMessage"), "danger")
    }, [])

    const handleAccountFormDataChange = (key: keyof UserFormData, value: any) => {
        setUserFormData({...userFormData, [key]: value})
    }

    const validateForm = () => {
        if (!!userSession?.['currentUser'])
            return setCanGoToNext(true)

        setCanGoToNext(Object.keys(accountFieldValidation)?.every(value =>
            accountFieldValidation[value].required ? !!userFormData[value] && accountFieldValidation[value].check : !userFormData[value] || accountFieldValidation[value].check))
    }

    const validateOrder = () => {
        setCanGoToNext(!!selectedPlan)
    }

    const handleNext = () => {
        if (currentStep === steps.length - 1)
            handleSubmit()
        else
            setCurrentStep(Math.min(steps.length - 1, currentStep + 1))
    }

    const handlePrevious = () => {
        setCurrentStep(Math.max(0, currentStep - 1))
    }

    useEffect(() => {
        switch (currentStep) {
            case 0:
                validateForm()
                break
            case 1:
                validateOrder()
                break
        }
    }, [userFormData, selectedPlan, currentStep])

    useEffect(() => {
        if (createCheckoutAsyncResult.isSuccess)
            openCheckout(createCheckoutAsyncResult.payload)
    }, [createCheckoutAsyncResult.isSuccess])

    // A signed-in visitor on the guest site pays from the app: the session cookie is shared, but
    // paying needs the Firebase sign-in, which only the app has. Same plan, same query string.
    const redirectsToApp = productionMode && !appHost && !!userSession

    useEffect(() => {
        if (redirectsToApp) {
            window.location.replace(`${APP_URL}/checkout${window.location.search}`)
            return
        }
        initPaymentAsyncResult.execute()
    }, [])

    useEffect(() => {
        if (searchParams.get("subscriptionId") && initPaymentAsyncResult.isSuccess)
            setSelectedPlan(availablePlans.find(value => value._id === searchParams.get("subscriptionId")))
    }, [searchParams, initPaymentAsyncResult.isSuccess])

    useEffect(() => {
        if (trialSubscriptionAsyncResult.isSuccess)
            goToDashboard()
    }, [trialSubscriptionAsyncResult.isSuccess])

    // Once the payment is confirmed, show the success state briefly, then open the dashboard.
    useEffect(() => {
        if (paymentStatus !== "successful") return
        const timer = setTimeout(goToDashboard, 2500)
        return () => clearTimeout(timer)
    }, [paymentStatus])


    if (redirectsToApp)
        return <LoadingPageComponent loadingText={t("plainLoadingText")}/>

    // @ts-ignore
    return <Stack sx={{backgroundColor: 'background.surface'}}>

        <Stack>
            <Stack paddingY={1} paddingX={2}>
                <Box
                    component="img"
                    width={{xs: "10rem", sm: "12rem"}}
                    height="auto"
                    src={uiTheme.fullLogo}
                    alt="LinguaScore"
                    sx={{cursor: "pointer"}}
                    onClick={() => navigate("/")}
                />
            </Stack>

            <Divider/>
        </Stack>

        {!initPaymentAsyncResult.isSuccess && !initPaymentAsyncResult.loading &&
            <LoadingError abstractText={t("defaultLoadingErrorMessage")} onRetry={initPaymentAsyncResult.execute}/>}

        {initPaymentAsyncResult.loading ?
            <LoadingPageComponent loadingText={t("plainLoadingText")}/> :
            initPaymentAsyncResult.payload &&
            <Grid container>
                <Grid xs={12} md={4} paddingY={2} paddingX={3}>
                    <Card variant={"outlined"} sx={{
                        padding: 2,
                        borderRadius: 15
                    }}>
                        <Stack gap={3}>
                            <Stack>
                                <Typography fontWeight={"bold"} textTransform={"uppercase"} level={"title-sm"}>
                                    {t("subscriptionPlanCardTitle")}
                                </Typography>

                                <Typography level={"body-sm"}>
                                    {t("subscriptionPlanCardDescription")}
                                </Typography>
                            </Stack>


                            <Stack gap={1}>
                                {availablePlans.map(plan =>
                                    <CheckoutPricingCardComponent
                                        onSelect={() => {
                                            const urlSearchParams = new URLSearchParams(searchParams)
                                            urlSearchParams.set("subscriptionId", plan._id)
                                            setSearchParams(urlSearchParams)
                                        }}
                                        selected={selectedPlan?._id === plan._id}
                                        subscription={plan}/>)}
                            </Stack>

                            <Divider/>

                            <Stack gap={1}>
                                {!selectedPlan?.isTrial &&
                                    <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                                        <Typography>
                                            {t("subscriptionPlanCardTotalLabel")}
                                        </Typography>

                                        <Typography level={"h4"}>
                                            {selectedPlan?.localizedPrice}
                                        </Typography>
                                    </Stack>}

                                <Typography level={"body-sm"} fontSize={12}>
                                    {t("checkoutPagePlatformDisclaimer")}
                                </Typography>
                            </Stack>

                        </Stack>
                    </Card>
                </Grid>

                <Grid xs={12} md={8} paddingX={{lg: 6, xs: 4}} paddingY={2}>
                    <Stack>
                        <Stepper sx={{width: '100%'}}>
                            {steps.map((step, index) => {
                                return <Step
                                    orientation="vertical"
                                    indicator={
                                        <StepIndicator color={"primary"}
                                                       variant={currentStep >= index + 1 ? "solid" : "outlined"}>
                                            {currentStep > index ? <Check style={{fontSize: 24}}/> :
                                                index + 1}
                                        </StepIndicator>
                                    }>
                                    {step.name}
                                </Step>;
                            })}
                        </Stepper>

                        {currentStep === 0 &&
                            <CheckoutAccountStep fieldValidation={accountFieldValidation}
                                                 onFormDataChange={handleAccountFormDataChange}
                                                 formData={userFormData}/>}

                        {currentStep === steps.length - 1 &&
                            <CheckoutReviewStep account={userFormData}
                                                onEditOrderInfo={step => setCurrentStep(step)}
                                                region={initPaymentAsyncResult.payload?.region}
                                                subscription={selectedPlan}/>}

                        <Stack direction={"row"} gap={2} justifyContent={"center"} paddingY={2}>
                            {currentStep > 0 &&
                                <Button variant={"outlined"} color={"neutral"} startDecorator={<ArrowLeft/>} size={"lg"}
                                        disabled={!canGoToNext} onClick={handlePrevious}>
                                    {t("previousCheckoutStepActionLabel")}
                                </Button>}

                            <Button size={"lg"}
                                    loading={auth.loading || createCheckoutAsyncResult.loading || trialSubscriptionAsyncResult.loading || !!checkoutUrl}
                                    endDecorator={currentStep === steps.length - 1 ? <></> : <ArrowRight/>}
                                    disabled={!canGoToNext} fullWidth onClick={handleNext}>
                                {steps[currentStep].actionLabel}
                            </Button>
                        </Stack>

                        {currentStep === steps.length - 1 && selectedPlan &&
                            (selectedPlan?.isTrial ?
                                    <Typography component="div">
                                        <Trans
                                            //@ts-ignore
                                            i18nKey={"confirmCheckoutInfoNotice"}
                                            components={{
                                                terms: <Link color={theme.palette.primary['400']}
                                                             to={"/terms-of-use"}/>
                                            }}/>
                                    </Typography>

                                    : <Typography component="div">
                                        <Trans
                                            //@ts-ignore
                                            i18nKey={"confirmCheckoutInfoAndPayNotice"}
                                            values={{
                                                localizedPrice: selectedPlan.localizedPrice
                                            }}
                                            components={{
                                                terms: <Link
                                                    style={{
                                                        color: theme.palette.primary["400"]
                                                    }}
                                                    to="/terms-of-use"
                                                />,
                                                bold: <span style={{fontWeight: 700}}/>
                                            }}
                                        />
                                    </Typography>
                            )}

                    </Stack>

                    <PaymentStatusDialog onClose={() => setShowPaymentStatusDialog(false)}
                                         onContinue={goToDashboard}
                                         open={showPaymentStatusDialog} status={paymentStatus}/>

                    <IkeepayCheckout checkoutUrl={checkoutUrl}
                                     onSuccess={handleCheckoutSuccess}
                                     onClose={handleCheckoutClose}
                                     onError={handleCheckoutError}/>

                </Grid>
            </Grid>}
    </Stack>
}

const CheckoutAccountStep = (props: CheckoutAccountStepProps) => {
    const {t} = useTranslation()
    const [passwordVisible, setPasswordVisible] = useState(false)
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false)
    const {userSession} = useSessionCookie()
    const currentUser = userSession?.['currentUser']

    const togglePasswordVisibility = () => setPasswordVisible(prevState => !prevState)
    const toggleConfirmPasswordVisibility = () => setConfirmPasswordVisible(prevState => !prevState)


    return <Box component={"form"} display={"flex"} flexDirection={"column"} gap={2}>

        <FormControl error={props.formData.name && !props.fieldValidation.name.check}>
            <FormLabel>
                {t("fullNameFieldLabel")}
            </FormLabel>
            <Input size={"lg"} startDecorator={<UserIcon/>}
                   value={props.formData.name}
                   onChange={(event) => props.onFormDataChange("name", event.target.value)}
                   placeholder={t("fullNameFieldPlaceholder")} name={"name"}/>

            {(props.formData.name && !props.fieldValidation.name.check) &&
                <FormHelperText>
                    {props.fieldValidation.name.message}
                </FormHelperText>}
        </FormControl>

        <FormControl error={props.formData.email && !props.fieldValidation.email.check}>
            <FormLabel>
                {t("emailFieldLabel")}
            </FormLabel>
            <Input size={"lg"} startDecorator={<LucideMail/>}
                   value={props.formData.email}
                   onChange={(event) => props.onFormDataChange("email", event.target.value)}
                   placeholder={t("emailFieldPlaceholder")} type={"email"}/>

            {(props.formData.email && !props.fieldValidation.email.check) &&
                <FormHelperText>
                    {props.fieldValidation.email.message}
                </FormHelperText>}
        </FormControl>

        <FormControl disabled={!!currentUser} error={props.formData.password && !props.fieldValidation.password.check}>
            <FormLabel>
                {t("passwordFieldLabel")}
            </FormLabel>
            <Input size={"lg"} startDecorator={<LockIcon/>}
                   endDecorator={passwordVisible ?
                       <EyeOff style={{cursor: "pointer"}} onClick={togglePasswordVisibility}/> :
                       <Eye onClick={togglePasswordVisibility} style={{cursor: "pointer"}}/>}
                   value={props.formData.password}
                   onChange={(event) => props.onFormDataChange("password", event.target.value)}
                   placeholder={t("passwordFieldPlaceholder")}
                   type={passwordVisible ? "text" : "password"}/>

            <FormHelperText>
                {(props.formData.password && !props.fieldValidation.password.check) ? props.fieldValidation.password.message : t("passwordFieldHelperText")}
            </FormHelperText>
        </FormControl>

        <FormControl disabled={!!currentUser}
                     error={(props.formData.confirmPassword && !props.fieldValidation.confirmPassword.check)}>
            <FormLabel>
                {t("confirmPasswordFieldLabel")}
            </FormLabel>
            <Input size={"lg"} startDecorator={<LockIcon/>}
                   endDecorator={confirmPasswordVisible ?
                       <EyeOff style={{cursor: "pointer"}} onClick={toggleConfirmPasswordVisibility}/> :
                       <Eye style={{cursor: "pointer"}} onClick={toggleConfirmPasswordVisibility}/>}
                   value={props.formData.confirmPassword}
                   onChange={(event) => props.onFormDataChange("confirmPassword", event.target.value)}
                   placeholder={t("confirmPasswordFieldPlaceholder")}
                   type={confirmPasswordVisible ? "text" : "password"}/>

            {(props.formData.confirmPassword && !props.fieldValidation.confirmPassword.check) &&
                <FormHelperText>
                    {props.fieldValidation.confirmPassword.message}
                </FormHelperText>}
        </FormControl>

        <Checkbox sx={{width: "fit-content", maxWidth: "100%"}} required
                  value={props.formData.confirmPassword}
                  onChange={(event) => props.onFormDataChange("agreedToTermsAndCondition", event.target.checked)}
                  label={t("termsAndConditionsFieldLabel")}/>
    </Box>
}


const CheckoutReviewStep = (props: CheckoutReviewStepProps) => {
    const {t} = useTranslation()
    return <Stack paddingY={2} gap={2}>
        <Stack>
            <Typography level={"h4"}>
                {t("confirmStepSubTitle")}
            </Typography>
            <Typography>
                {t("confirmStepDescription")}
            </Typography>
        </Stack>

        {props.account &&
            <Card variant={"soft"} sx={{gap: 0, padding: 2}}>
                <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
                    <Typography level={"body-sm"} textTransform={"uppercase"}>
                        {t("accountStepTitle")}
                    </Typography>

                    <Button onClick={() => props.onEditOrderInfo(0)} size={"sm"} variant={"plain"}>
                        {t("editStepDetailActionLabel")}
                    </Button>
                </Stack>

                <Stack direction={"row"} gap={2} alignItems={"center"}>
                    <Avatar color={"primary"}>
                        <UserIcon/>
                    </Avatar>

                    <Stack>
                        <Typography level={"title-lg"}>
                            {props.account?.name}
                        </Typography>

                        <Typography level={"body-sm"}>
                            {props.account?.email}
                        </Typography>
                    </Stack>

                </Stack>
            </Card>}

        {props.subscription && !props.subscription.isTrial &&
            <Card variant={"soft"} sx={{gap: 0, padding: 2}}>
                <Typography level={"body-sm"} textTransform={"uppercase"} paddingBottom={1}>
                    {t("paymentStepTitle")}
                </Typography>

                <Stack direction={"row"} gap={2} alignItems={"center"}>
                    <Avatar color={"primary"}>
                        <CreditCard/>
                    </Avatar>

                    <Stack>
                        <Typography level={"title-lg"}>
                            {t("ikeepayPaymentMethodTitle")}
                        </Typography>

                        <Typography level={"body-sm"}>
                            {t("ikeepayPaymentMethodDescription")}
                        </Typography>
                    </Stack>

                </Stack>
            </Card>}
    </Stack>
}

export default Checkout